import React, { useCallback, useMemo, useState } from 'react';

const ActiveOrgContext = React.createContext(null);

const STORAGE_PREFIX = 'ao-active-org:';


function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || ''}`;
}


function readStored(userId) {
  if (!userId) {
    return null;
  }
  try {
    return window.localStorage.getItem(storageKey(userId)) || null;
  }
  catch {
    // Storage unavailable (private mode / SSR) — no persisted selection.
    return null;
  }
}


/** Resolve the active membership from a stored id, falling back to the first
 * membership when the stored one is gone (org left/deleted) or absent. */
function resolveMembership(memberships, storedId) {
  const list = memberships || [];
  if (storedId) {
    const match = list.find((entry) => String(entry.id) === String(storedId));
    if (match) {
      return match;
    }
  }
  return list[0] || null;
}


/** Owns the selected membership across the app (#213). The selection is the
 * membership id, persisted in localStorage keyed by USER id so two accounts on
 * one browser never inherit each other's org. Switching orgs changes every
 * seller-scoped query key, so React Query refetches naturally; consumers that
 * keep transient per-org UI state (Listings selection) re-key on the seller id.
 *
 * `memberships` and `userId` are supplied by the /app shell (it already runs the
 * mine + me queries), so this provider does no fetching of its own. */
function ActiveOrgProvider({ memberships, userId, children }) {
  // Selected membership id; lazy-init from storage (writes happen in the handler
  // and the re-key branch below, never during render).
  const [selectedId, setSelectedId] = useState(() => readStored(userId));
  // Track the user the selection belongs to; a new user re-reads persistence.
  const [storageUser, setStorageUser] = useState(userId);

  if (storageUser !== userId) {
    setStorageUser(userId);
    setSelectedId(readStored(userId));
  }

  const membership = resolveMembership(memberships, selectedId);

  const setActiveMembership = useCallback((membershipId) => {
    setSelectedId(membershipId);
    try {
      if (userId) {
        window.localStorage.setItem(storageKey(userId), String(membershipId));
      }
    }
    catch {
      // Persisting is best-effort.
    }
  }, [userId]);

  const contextValue = useMemo(() => ({
    membership,
    seller: membership?.seller || null,
    memberships: memberships || [],
    setActiveMembership,
  }), [membership, memberships, setActiveMembership]);

  return <ActiveOrgContext.Provider value={contextValue}>{children}</ActiveOrgContext.Provider>;
}

ActiveOrgProvider.displayName = 'ActiveOrgProvider';
export { ActiveOrgContext, ActiveOrgProvider };
